import React, { useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import Modal from '../../../../../components/modals/Modal';
import { FormInput, FormTextArea, FormCheckBox, SelectInput } from '../../../../../components/form';
import { Button } from '../../../../../components/common';
import plansService from '../../../../../services/api/smartbill/plansService';


const validationSchema = Yup.object().shape({
    name: Yup.string().required('Plan name is required'),
    description: Yup.string().required('Description is required'),
    pricePerStudent: Yup.number()
        .min(0, 'Price must be greater than or equal to 0')
        .required('Price per student is required'),
    currency: Yup.string().required('Currency is required'),
});


function ModalCreatePlan({ open, plan, onClose, onSuccess }: Props) {
    const [submitStatus, setSubmitStatus] = useState({ loading: false, failedMessage: '' });

    useEffect(() => {
        if (open) {
            setSubmitStatus({ loading: false, failedMessage: '' });
        }
    }, [open]);

    const handleFormSubmit = async (formData: any, { resetForm }: any) => {
        setSubmitStatus({ loading: true, failedMessage: '' });

        try {
            const result = plan 
                ? await plansService.updatePlan(plan.id, formData)
                : await plansService.createPlan(formData);

            if (result.success) {
                resetForm();
                onClose();
                onSuccess();
                setSubmitStatus({ loading: false, failedMessage: '' });
            } else {
                setSubmitStatus({ loading: false, failedMessage: result.message });
            }
        } catch (error) {
            setSubmitStatus({ loading: false, failedMessage: 'An unexpected error occurred' });
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <div className="inline-block w-[32rem] max-w-full">
                <div className="flex justify-between mb-5 px-10 pt-8">
                    <h2 className="font-medium text-xl">
                        {plan ? 'Edit Plan' : 'Create Plan'}
                    </h2>
                    <button type="button" onClick={onClose} className="text-2xl text-gray-500 hover:text-gray-600 dark:text-gray-600 dark:hover:text-gray-500 focus:outline-none">
                        <i className="ri-close-line"></i>
                    </button>
                </div>
                <div className='max-h-[80vh] overflow-y-auto px-10 pb-6'>
                    <Formik
                        initialValues={{
                            name: plan?.name || '',
                            description: plan?.description || '',
                            pricePerStudent: plan?.pricePerStudent || 0,
                            currency: plan?.currency || 'USD',
                            defaultPlan: plan?.defaultPlan || false,
                            active: plan?.active !== undefined ? plan.active : true,
                        }}
                        validationSchema={validationSchema}
                        onSubmit={handleFormSubmit}
                    >
                        {({ values, errors, touched, setFieldValue }) => (
                            <Form>
                                {submitStatus.failedMessage && !submitStatus.loading && (
                                    <p className='text-red-500 mb-4'>{submitStatus.failedMessage}</p>
                                )}

                                <div className="mb-6">
                                    <label htmlFor="input-name" className='block text-sm mb-2'>Plan Name</label>
                                    <FormInput 
                                        type="text" 
                                        name="name" 
                                        id="input-name" 
                                        placeholder="Enter plan name" 
                                    />
                                    {touched.name && errors.name && (
                                        <p className='text-sm text-red-500 mt-2'>{errors.name as string}</p>
                                    )}
                                </div>

                                <div className="mb-6">
                                    <label htmlFor="input-description" className='block text-sm mb-2'>Description</label>
                                    <FormTextArea 
                                        name='description' 
                                        id="input-description" 
                                        placeholder="Enter plan description" 
                                        rows={3} 
                                    />
                                    {touched.description && errors.description && (
                                        <p className='text-sm text-red-500 mt-2'>{errors.description as string}</p>
                                    )}
                                </div>

                                <div className="mb-6">
                                    <label htmlFor="input-price" className='block text-sm mb-2'>Price per Student</label>
                                    <FormInput 
                                        type="number" 
                                        name="pricePerStudent" 
                                        id="input-price" 
                                        placeholder="0.00" 
                                        step="0.01"
                                        min="0"
                                    />
                                    {touched.pricePerStudent && errors.pricePerStudent && (
                                        <p className='text-sm text-red-500 mt-2'>{errors.pricePerStudent as string}</p>
                                    )}
                                </div>

                                <div className="mb-6">
                                    <label htmlFor="input-currency" className='block text-sm mb-2'>Currency</label>
                                    <SelectInput
                                        items={[
                                            { label: 'USD - US Dollar', value: 'USD' },
                                            { label: 'EUR - Euro', value: 'EUR' },
                                            { label: 'GBP - British Pound', value: 'GBP' },
                                            { label: 'SGD - Singapore Dollar', value: 'SGD' },
                                            { label: 'GHS - Ghana Cedi', value: 'GHS' },
                                        ]}
                                        placeholder="Select currency"
                                        value={values.currency}
                                        onChange={(selected: any) => setFieldValue('currency', selected.value)}
                                    />
                                    {touched.currency && errors.currency && (
                                        <p className='text-sm text-red-500 mt-2'>{errors.currency as string}</p>
                                    )}
                                </div>

                                <div className="mb-6">
                                    <label className='block text-sm mb-2'>Plan Settings</label>
                                    <div className="space-y-3">
                                        <div className="flex items-center space-x-3">
                                            <FormCheckBox
                                                name="defaultPlan"
                                                id="input-default"
                                                checked={values.defaultPlan}
                                                onChange={(e) => setFieldValue('defaultPlan', e.target.checked)}
                                            />
                                            <label htmlFor="input-default" className='text-sm cursor-pointer'>
                                                Default Plan
                                            </label>
                                        </div>
                                        
                                        <div className="flex items-center space-x-3">
                                            <FormCheckBox
                                                name="active"
                                                id="input-active"
                                                checked={values.active}
                                                onChange={(e) => setFieldValue('active', e.target.checked)}
                                            />
                                            <label htmlFor="input-active" className='text-sm cursor-pointer'>
                                                Active Status
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div className='flex justify-end items-center'>
                                    <Button 
                                        type='button' 
                                        onClick={onClose} 
                                        className='!text-gray-500 dark:!text-white bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-600 py-2.5 mr-6'
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type='submit'
                                        loading={submitStatus.loading}
                                        className=''
                                        disabled={submitStatus.loading}
                                    >
                                        {plan ? 'Update Plan' : 'Create Plan'}
                                    </Button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>
        </Modal>
    );
}


interface Props {
    open: boolean;
    plan?: any;
    onClose: () => void;
    onSuccess: () => void;
}


export default ModalCreatePlan;